import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createNoteAction, deleteNoteAction, getNotesAction, togglePinNoteAction, updateNoteAction } from "../action/notes.actions";
import { CreateNotePayload, INote, INoteFilters, TogglePinNotePayload, UpdateNotePayload } from "../interfaces/notes.interface";

export const notesKeys = {
  all: ["notes"] as const,
  lists: () => [...notesKeys.all, "list"] as const,
  list: (filters: INoteFilters) => [...notesKeys.lists(), filters] as const,
  details: () => [...notesKeys.all, "detail"] as const,
  detail: (id: string) => [...notesKeys.details(), id] as const,
};

export const useNotesQuery = (filters: INoteFilters = {}, options: { enabled?: boolean } = {}) => {
  const isEnabled = options.enabled !== undefined ? options.enabled : Boolean(filters.clientUid || filters.jobId || filters.jobEventUid);

  return useQuery<INote[], Error>({
    queryKey: notesKeys.list(filters),
    queryFn: () => getNotesAction(filters),
    enabled: isEnabled,
  });
};

export const useCreateNoteMutation = (options?: { onSuccess?: (note: INote) => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<INote, Error, CreateNotePayload>({
    mutationFn: createNoteAction,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useUpdateNoteMutation = (options?: { onSuccess?: (note: INote) => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<INote, Error, UpdateNotePayload>({
    mutationFn: updateNoteAction,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useTogglePinNoteMutation = (options?: { onSuccess?: (note: INote) => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<INote, Error, TogglePinNotePayload>({
    mutationFn: togglePinNoteAction,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export const useDeleteNoteMutation = (options?: { onSuccess?: () => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, string>({
    mutationFn: deleteNoteAction,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notesKeys.all });
      options?.onSuccess?.();
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};

export type UseNotesParams =
  | string
  | {
      clientUid?: string | null;
      jobId?: number | null;
      jobEventUid?: string | null;
    };

export const useClientNotes = (params: UseNotesParams | null | undefined) => {
  const clientUid = typeof params === "string" ? params : params?.clientUid;
  const jobId = typeof params === "object" ? params?.jobId : undefined;
  const jobEventUid = typeof params === "object" ? params?.jobEventUid : undefined;

  const filters: INoteFilters = jobEventUid
    ? { jobEventUid }
    : {
        clientUid: clientUid || undefined,
        jobId: jobId || undefined,
      };

  const query = useNotesQuery(filters, { enabled: Boolean(clientUid || jobId || jobEventUid) });

  const createMutation = useCreateNoteMutation();
  const updateMutation = useUpdateNoteMutation();
  const pinMutation = useTogglePinNoteMutation();
  const deleteMutation = useDeleteNoteMutation();

  const createNote = async (noteParams: {
    content: string;
    type?: string;
    pinned?: boolean;
    jobId?: number | null;
    jobEventUid?: string | null;
    clientUid?: string | null;
  }) => {
    const finalClientUid = noteParams.clientUid || clientUid;
    if (!finalClientUid) throw new Error("Client UID is required to create a note");
    return createMutation.mutateAsync({
      clientUid: finalClientUid,
      content: noteParams.content,
      type: noteParams.type,
      pinned: noteParams.pinned,
      jobId: noteParams.jobId !== undefined ? noteParams.jobId : jobId || null,
      jobEventUid: noteParams.jobEventUid !== undefined ? noteParams.jobEventUid : jobEventUid || null,
    });
  };

  const updateNote = async (params: { noteUid: string; content?: string; type?: string; pinned?: boolean }) => {
    return updateMutation.mutateAsync(params);
  };

  const togglePin = async (params: { noteUid: string; pinned?: boolean }) => {
    return pinMutation.mutateAsync(params);
  };

  const deleteNote = async (noteUid: string) => {
    return deleteMutation.mutateAsync(noteUid);
  };

  return {
    notes: query.data || [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    createNote,
    updateNote,
    togglePin,
    deleteNote,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isPinning: pinMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSaving: createMutation.isPending || updateMutation.isPending || pinMutation.isPending || deleteMutation.isPending,
  };
};
